/**
 * Formats a JavaScript Date object into a human-readable string.
 *
 * @param {Date} date - The date to format.
 * @param {"short" | "long"} type - The formatting style ("short" = numeric date, "long" = full date).
 * @returns {string} A locale-formatted date string.
 *
 * @description
 * Returns a UTC-based formatted date string.  
 * The "long" option uses full month names, while "short" keeps numeric formatting.
 */
export const dateFormatter = (date, type) => {
    const longOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
    };
    const shortOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        timeZone: 'UTC',
    };
    return date.toLocaleDateString('en-US', type === 'long' ? longOptions : shortOptions)
}

/**
 * Formats a Date object into a localized time string (HH:MM).
 *
 * @param {Date} date - The date object from which to extract the time.
 * @returns {string} The formatted time (e.g., "14:30").
 *
 * @description
 * Produces a short, localized time string using the 'es-ES' locale.
 */
export const timeFormatter = (date) => {
    return date.toLocaleTimeString('es-ES', { hour: "2-digit", minute: "2-digit" })
}

/**
 * Formats a predefined time range code into a readable description.
 *
 * @param {"5D"|"1M"|"6M"|"YTD"|"1Y"|"5Y"|"MAX"} range - The selected time range.
 * @returns {string} A descriptive string explaining the range.
 *
 * @description
 * Converts short time range codes (e.g., "1M", "YTD") into descriptive phrases.  
 */
export const timeRangeFormatter = (range) => {
    switch (range) {
        case ('5D'):
            return "in the last 5 days"
        case ('1M'):
            return "in the last month"
        case ('6M'):
            return "in the last 6 months"
        case ('YTD'):
            return "year to date"
        case ('1Y'):
            return "in the last year"
        case ('5Y'):
            return "in the last 5 years"
        case ('MAX'):
            return "in all recorded history"
        default:
            return "in all recorded history"
    }
}

/**
 * Converts a Date object into a standardized API-compatible date string.
 *
 * @param {Date} date - The date object to format.
 * @returns {string} A string in `YYYY-MM-DD` format.
 *
 * @description
 * Converts a full ISO timestamp into a simplified date string by truncating the time portion.  
 * Example: `new Date("2025-11-12T15:30:00Z") → "2025-11-12"`.
 */
export const APIDateStringFormatter = (date) => {
    return date.toISOString().slice(0, 10)
}